import { useReducer, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import { VideoPlayerActionsEnum } from '../utils/enum';

interface UseVideoPlayerStateProps {
	sound?: boolean;
	playing?: boolean;
	minimized?: boolean;
}

interface VideoPlayerState {
	volume: number;
	duration: number;
	minimized: boolean;
	playbackRate: number;
	isCollapsed: boolean;
	isDraggable: boolean;
	playedSeconds: number;
	playingState: boolean;
	seeking: boolean;
}

export type VideoPlayerAction =
	| { type: VideoPlayerActionsEnum.TOGGLE_PLAYING_STATE }
	| { type: VideoPlayerActionsEnum.SET_VOLUME; payload: number }
	| { type: VideoPlayerActionsEnum.SET_DURATION; payload: number }
	| { type: VideoPlayerActionsEnum.SET_MINIMIZED; payload: boolean }
	| { type: VideoPlayerActionsEnum.SET_PLAYBACK_RATE; payload: number }
	| { type: VideoPlayerActionsEnum.SET_IS_DRAGGABLE; payload: boolean }
	| { type: VideoPlayerActionsEnum.SET_IS_COLLAPSED; payload: boolean }
	| { type: VideoPlayerActionsEnum.SET_PLAYED_SECONDS; payload: number }
	| { type: VideoPlayerActionsEnum.SET_PLAYING_STATE; payload: boolean }
	| { type: VideoPlayerActionsEnum.SET_SEEKING; payload: boolean }
	| { type: VideoPlayerActionsEnum.SET_MULTIPLE; payload: Partial<VideoPlayerState> };

const initialState: VideoPlayerState = {
	volume: 1,
	duration: 0,
	playbackRate: 1,
	playedSeconds: 0,
	minimized: false,
	isDraggable: false,
	isCollapsed: false,
	playingState: false,
	seeking: false
};

type ActionHandler = (state: VideoPlayerState, action: VideoPlayerAction) => VideoPlayerState;

const actionHandlers: Record<VideoPlayerAction['type'], ActionHandler> = {
	SET_IS_DRAGGABLE: (state, action) => ({
		...state,
		isDraggable: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_IS_DRAGGABLE }>).payload
	}),
	SET_IS_COLLAPSED: (state, action) => ({
		...state,
		isCollapsed: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_IS_COLLAPSED }>).payload
	}),
	SET_DURATION: (state, action) => ({
		...state,
		duration: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_DURATION }>).payload
	}),
	SET_VOLUME: (state, action) => ({
		...state,
		volume: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_VOLUME }>).payload
	}),
	SET_PLAYBACK_RATE: (state, action) => ({
		...state,
		playbackRate: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_PLAYBACK_RATE }>).payload
	}),
	SET_PLAYED_SECONDS: (state, action) => ({
		...state,
		playedSeconds: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_PLAYED_SECONDS }>).payload
	}),
	SET_PLAYING_STATE: (state, action) => ({
		...state,
		playingState: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_PLAYING_STATE }>).payload
	}),
	SET_MINIMIZED: (state, action) => ({
		...state,
		minimized: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_MINIMIZED }>).payload
	}),
	TOGGLE_PLAYING_STATE: (state) => ({
		...state,
		playingState: !state.playingState
	}),
	SET_SEEKING: (state, action) => ({
		...state,
		seeking: (action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_SEEKING }>).payload
	}),
	SET_MULTIPLE: (state, action) => ({
		...state,
		...(action as Extract<VideoPlayerAction, { type: VideoPlayerActionsEnum.SET_MULTIPLE }>).payload
	})
};

function videoPlayerReducer(state: VideoPlayerState, action: VideoPlayerAction): Readonly<VideoPlayerState> {
	const handler = actionHandlers[action.type];
	return handler ? handler(state, action) : state;
}

export function useVideoPlayerState({
	sound = true,
	playing = false,
	minimized: minimizedProp
}: UseVideoPlayerStateProps) {
	const [state, dispatch] = useReducer(videoPlayerReducer, {
		...initialState,
		volume: sound ? 1 : 0,
		playingState: playing,
		minimized: minimizedProp ?? false,
		isDraggable: minimizedProp ?? false
	});

	const playerRef = useRef<ReactPlayer>(null);
	const previousVolumeRef = useRef<number>(1);

	useEffect(() => {
		dispatch({ type: VideoPlayerActionsEnum.SET_PLAYING_STATE, payload: playing });
	}, [playing]);

	useEffect(() => {
		dispatch({ type: VideoPlayerActionsEnum.SET_VOLUME, payload: sound ? 1 : 0 });
	}, [sound]);

	useEffect(() => {
		if (minimizedProp !== undefined) {
			dispatch({
				type: VideoPlayerActionsEnum.SET_MULTIPLE,
				payload: { minimized: minimizedProp, isDraggable: minimizedProp }
			});
		}
	}, [minimizedProp]);

	return {
		...state,
		dispatch,
		playerRef,
		previousVolumeRef
	};
}
